import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  isSameMonth,
  isSameDay,
  isToday,
  startOfDay,
} from 'date-fns';
import EventModal from './EventModal';
import { loadCalendarEvents, saveCalendarEvents, deleteFinalizedEvent } from '../services/calendarSheetService';

const VIEW_MODES = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEK_HEADER_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SkeletonLoading = () => (
  <div className="bg-white rounded-lg shadow border border-gray-200 animate-pulse">
    {/* Header skeleton */}
    <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
      {WEEK_HEADER_KEYS.map((k) => (
        <div key={k} className="py-3 text-center border-r border-gray-200 last:border-r-0">
          <div className="h-3 bg-gray-200 rounded mx-auto w-8" />
        </div>
      ))}
    </div>
    {/* Week rows skeleton */}
    {Array.from({ length: 5 }, (_, row) => (
      <div key={row} className="grid grid-cols-7">
        {Array.from({ length: 7 }, (_, col) => (
          <div
            key={col}
            className="min-h-[100px] p-2 border-b border-r border-gray-200 last:border-r-0"
          >
            <div className="h-5 w-5 bg-gray-200 rounded-full mx-auto mb-2" />
            <div className="space-y-1.5">
              <div className="h-3 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Calendar = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const scrollContainerRef = React.useRef(null);
  const hasScrolledRef = React.useRef(false);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Scroll to current time once per view change
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [viewMode]);

  useEffect(() => {
    if ((viewMode === 'day' || viewMode === 'week') && !hasScrolledRef.current) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`hour-${currentTime.getHours()}`);
        if (el && scrollContainerRef.current) {
          const c = scrollContainerRef.current;
          const top = el.offsetTop - 20;
          c.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          hasScrolledRef.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [viewMode, currentTime]);

  // Load events
  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    // Load from service (localStorage first, proxy in background)
    const result = await loadCalendarEvents();
    const loadedEvents = result.events || [];

    setEvents(loadedEvents);
    setLoading(false);
  };

  const saveEvents = async (updated) => {
    setEvents(updated);

    // Save via proxy (server-side Google Sheet + in-memory cache)
    await saveCalendarEvents(updated);

    // Also save to old KV API as extra fallback
    try {
      await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: updated }),
      });
    } catch {
      // ignore
    }

    // Always save locally for offline recovery
    localStorage.setItem('calendar_events', JSON.stringify(updated));
  };

  const navigate = (dir) => {
    const fn = dir > 0
      ? { day: addDays, week: addWeeks, month: addMonths }
      : { day: subDays, week: subWeeks, month: subMonths };
    setCurrentDate(fn[viewMode](currentDate, 1));
  };

  const navigateToday = () => setCurrentDate(new Date());

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleSlotClick = (date, hour) => {
    setSelectedDate(date);
    setSelectedSlot(hour);
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setSelectedDate(null);
    setSelectedSlot(null);
    setShowEventModal(true);
  };

  const handleEventSave = async (eventData) => {
    let updated;
    if (selectedEvent) {
      updated = events.map((e) =>
        e.id === selectedEvent.id ? { ...eventData, id: selectedEvent.id } : e
      );
      // If event was finalized and is being edited, remove from FinalizedEvents
      if (selectedEvent.isFinalized) {
        await deleteFinalizedEvent(selectedEvent.id);
      }
    } else {
      updated = [...events, { ...eventData, id: Date.now().toString() }];
    }
    saveEvents(updated);
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleReschedule = async (newEvent) => {
    const updated = [...events, newEvent];
    await saveEvents(updated);

    // If the original event was finalized, remove it from FinalizedEvents
    if (selectedEvent && selectedEvent.isFinalized) {
      await deleteFinalizedEvent(selectedEvent.id);
    }

    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleEventDelete = async () => {
    if (selectedEvent) {
      // Remove from FinalizedEvents if it was finalized
      if (selectedEvent.isFinalized) {
        await deleteFinalizedEvent(selectedEvent.id);
      }

      saveEvents(events.filter((e) => e.id !== selectedEvent.id));
      setShowEventModal(false);
      setSelectedEvent(null);
    }
  };

  const handleFinalize = async (evt) => {
    // Mark the event as finalized in the calendar (keep it, just update flag)
    const updatedEvents = events.map((e) =>
      e.id === evt.id ? { ...e, isFinalized: true } : e
    );
    saveEvents(updatedEvents);

    // Submit finalized record to FinalizedEvents sheet via the proxy
    try {
      await fetch('/api/calendar-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheet: 'FinalizedEvents',
          finalizedEvent: {
            id: evt.id,
            userName: evt.userName,
            phone: evt.phone,
            propertyName: evt.propertyName,
            propertyId: evt.propertyId,
            district: evt.district,
            date: evt.date,
            notes: evt.notes,
            propertyLink: evt.propertyLink,
            isRevisit: evt.isRevisit || false,
            revisitOfId: evt.revisitOfId || '',
            finalizedAt: new Date().toISOString(),
          },
        }),
      });
    } catch {
      // best-effort
    }

    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const eventsForDate = useCallback(
    (date) =>
      events.filter((e) => isSameDay(startOfDay(new Date(e.date)), startOfDay(date))),
    [events]
  );

  // Compute month days — wrapped in { start, end } for date-fns v4
  const monthDays = useMemo(() => {
    const s = startOfMonth(currentDate);
    const e = endOfMonth(currentDate);
    return eachDayOfInterval({
      start: startOfWeek(s, { weekStartsOn: 1 }),
      end: endOfWeek(e, { weekStartsOn: 1 }),
    });
  }, [currentDate]);

  // Compute week days — wrapped in { start, end } for date-fns v4
  const weekDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      }),
    [currentDate]
  );

  const headerTitle = () => {
    switch (viewMode) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week': {
        const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
        const we = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(ws, 'MMM d')} - ${format(we, 'MMM d, yyyy')}`;
      }
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      default:
        return '';
    }
  };

  const weekDayLabels = () => [
    { key: 'Mon', label: t('calendar.mon') },
    { key: 'Tue', label: t('calendar.tue') },
    { key: 'Wed', label: t('calendar.wed') },
    { key: 'Thu', label: t('calendar.thu') },
    { key: 'Fri', label: t('calendar.fri') },
    { key: 'Sat', label: t('calendar.sat') },
    { key: 'Sun', label: t('calendar.sun') },
  ];

  if (loading) {
    return (
      <div>
        {/* Header placeholder */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 animate-pulse">
            <div className="flex items-center gap-2">
              <div className="h-10 w-20 bg-gray-200 rounded-lg" />
              <div className="h-10 w-10 bg-gray-200 rounded-lg" />
              <div className="h-10 w-10 bg-gray-200 rounded-lg" />
              <div className="h-6 w-48 bg-gray-200 rounded ml-2" />
            </div>
            <div className="h-10 w-64 bg-gray-200 rounded-lg" />
          </div>
        </div>
        {/* Calendar skeleton */}
        <SkeletonLoading />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={navigateToday}
              className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50"
            >
              {t('calendar.today')}
            </button>
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={() => navigate(1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-gray-900 ml-2">{headerTitle()}</h2>
          </div>
          <div className="flex rounded-lg border">
            {['day', 'week', 'month'].map((mode, i) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-4 py-2 text-sm font-medium ${
                  i === 0 ? 'rounded-l-lg' : i === 2 ? 'rounded-r-lg' : ''
                } ${i === 1 ? 'border-x' : ''} ${
                  viewMode === mode
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t(`calendar.${mode}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Month View — MS Teams style */}
      {viewMode === 'month' && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {weekDayLabels().map((d, i) => (
              <div
                key={d.key}
                className={`py-3 text-center text-sm font-semibold text-gray-700 ${
                  i < 6 ? 'border-r border-gray-200' : ''
                }`}
              >
                {d.label}
              </div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7">
            {monthDays.map((day, idx) => {
              const dayEvts = eventsForDate(day);
              const inMonth = isSameMonth(day, currentDate);
              const lastCol = idx % 7 === 6;
              const today = isToday(day);

              return (
                <div
                  key={idx}
                  onClick={() => handleDayClick(day)}
                  className={`min-h-[110px] p-1.5 cursor-pointer hover:bg-gray-50 transition-colors border-b ${
                    lastCol ? '' : 'border-r'
                  } border-gray-200 ${
                    !inMonth ? 'bg-gray-50' : today ? 'bg-blue-50' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-center mb-1">
                    <div
                      className={`text-sm font-medium inline-flex items-center justify-center w-7 h-7 ${
                        today
                          ? 'bg-primary-600 text-white rounded-full'
                          : inMonth
                          ? 'text-gray-900'
                          : 'text-gray-400'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    {dayEvts.slice(0, 3).map((evt) => (
                      <div
                        key={evt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(evt);
                        }}
                        className={`text-[11px] leading-tight ${
                          evt.isFinalized
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : evt.isRevisit
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        } px-1.5 py-0.5 rounded truncate cursor-pointer`}
                      >
                        {evt.isFinalized && <span className="mr-0.5">✅</span>}
                        {evt.isRevisit && <span className="mr-0.5">🔁</span>}
                        <span className="font-medium">{format(new Date(evt.date), 'HH:mm')}</span>{' '}
                        {evt.userName}
                      </div>
                    ))}
                    {dayEvts.length > 3 && (
                      <div className="text-[11px] text-gray-500 text-center">
                        +{dayEvts.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View — MS Teams style */}
      {viewMode === 'week' && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          {/* Day header row */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200 bg-gray-50">
            <div className="p-2 border-r border-gray-200" />
            {weekDays.map((day) => {
              const today = isToday(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`p-2 text-center border-r border-gray-200 last:border-r-0 ${
                    today ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="text-xs text-gray-500 font-medium">{format(day, 'EEE')}</div>
                  <div
                    className={`text-base font-semibold mt-0.5 inline-flex items-center justify-center w-8 h-8 ${
                      today ? 'bg-primary-600 text-white rounded-full' : 'text-gray-900'
                    }`}
                  >
                    {format(day, 'd')}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Time slots */}
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto"
            style={{ maxHeight: '600px' }}
          >
            {HOURS.map((hour) => {
              const isCurrentHourRow = isSameDay(currentDate, currentTime) && hour === currentTime.getHours();

              return (
                <div
                  key={hour}
                  id={`hour-${hour}`}
                  className={`grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200 ${
                    isCurrentHourRow ? 'bg-blue-50' : ''
                  }`}
                >
                  {/* Time label */}
                  <div className="p-1 text-[11px] text-gray-500 border-r border-gray-200 text-right pr-2 bg-gray-50 font-medium">
                    {String(hour).padStart(2, '0')}:00
                  </div>
                  {/* Day columns */}
                  {weekDays.map((day, idx) => {
                    const hourEvts = events.filter((e) => {
                      const ed = new Date(e.date);
                      return isSameDay(startOfDay(ed), day) && ed.getHours() === hour;
                    });
                    const isNow = isSameDay(day, currentTime) && hour === currentTime.getHours();

                    return (
                      <div
                        key={`${day.toISOString()}-${hour}`}
                        onClick={() => handleSlotClick(day, hour)}
                        className={`min-h-[48px] border-r border-gray-200 hover:bg-gray-100 cursor-pointer relative ${
                          idx === 6 ? 'border-r-0' : ''
                        }`}
                      >
                        {isNow && (
                          <div className="absolute left-0 right-0 top-0 h-0.5 bg-red-500 z-10 pointer-events-none">
                            <div className="absolute -left-1 -top-1.5 w-2 h-2 bg-red-500 rounded-full" />
                          </div>
                        )}
                        {hourEvts.map((evt) => (
                          <div
                            key={evt.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(evt);
                            }}
                            className={`text-[11px] px-1 py-0.5 m-0.5 rounded truncate cursor-pointer ${
                              evt.isFinalized
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : evt.isRevisit
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                            }`}
                          >
                            {evt.isFinalized && <span className="mr-0.5">✅</span>}
                            {evt.isRevisit && <span className="mr-0.5">🔁</span>}
                            <span className="font-medium">{evt.userName}</span>
                            {evt.propertyName && (
                              <span className="text-gray-500"> - {evt.propertyName}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
            <p className="text-sm text-gray-600">
              {eventsForDate(currentDate).length} {t('calendar.events')}
            </p>
          </div>
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto"
            style={{ maxHeight: '600px' }}
          >
            {HOURS.map((hour) => {
              const hourEvts = eventsForDate(currentDate).filter(
                (e) => new Date(e.date).getHours() === hour
              );
              const isNow = isSameDay(currentDate, currentTime) && hour === currentTime.getHours();

              return (
                <div
                  key={hour}
                  id={`hour-${hour}`}
                  className={`flex border-b last:border-b-0 ${isNow ? 'bg-blue-50' : ''}`}
                >
                  {/* Time label */}
                  <div className="w-16 flex-shrink-0 p-3 text-sm text-gray-500 border-r text-right">
                    {String(hour).padStart(2, '0')}:00
                  </div>
                  {/* Content area */}
                  <div
                    onClick={() => handleSlotClick(currentDate, hour)}
                    className="flex-1 min-h-[60px] hover:bg-gray-50 cursor-pointer relative p-2"
                  >
                    {isNow && (
                      <div className="absolute left-0 right-0 top-0 h-0.5 bg-red-500 z-10 pointer-events-none">
                        <div className="absolute -left-1 -top-1.5 w-2 h-2 bg-red-500 rounded-full" />
                      </div>
                    )}
                    <div className="space-y-1">
                      {hourEvts.map((evt) => (
                        <div
                          key={evt.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(evt);
                          }}
                          className={`${
                            evt.isFinalized
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300'
                              : evt.isRevisit
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300'
                              : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                          } p-3 rounded-lg cursor-pointer`}
                        >
                          {evt.isFinalized && (
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs font-semibold bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">✅ {t('calendar.finalizedLabel')}</span>
                            </div>
                          )}
                          {evt.isRevisit && (
                            <div className="flex items-center gap-1 mb-1">
                              <span className="text-xs font-semibold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">🔁 {t('calendar.revisitLabel')}</span>
                            </div>
                          )}
                          <div className="font-semibold">{evt.userName}</div>
                          <div className="text-sm">{evt.phone}</div>
                          <div className="text-sm">📍 {evt.propertyName}</div>
                          {evt.propertyLink && (
                            <a
                              href={evt.propertyLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-primary-600 hover:underline mt-1 inline-block"
                            >
                              🔗 View Property
                            </a>
                          )}
                          <div className="text-xs text-gray-600 mt-1">
                            {format(new Date(evt.date), 'h:mm a')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
          onReschedule={selectedEvent ? handleReschedule : null}
          onFinalize={selectedEvent ? handleFinalize : null}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
            setSelectedDate(null);
            setSelectedSlot(null);
          }}
        />
      )}
    </div>
  );
};

export default Calendar;
export { VIEW_MODES };